import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "next-i18next";
import classNames from "classnames";

// Components
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";
import { copyToClipboard } from "@vahak/core/dist/methods/copyToClipboard";
import { cityStringIncludeParentheses } from "@vahak/core/dist/methods/cityStringIncludeParentheses";
import A from "@vahak/core/dist/components/app-anchor/A";
import Activation from "./Activation";
import { AppContext } from "@vahak/core/dist/app-context";

// Style
import Styles from "./ShareModal.module.scss";

// Assets
import Whatsapp from "@vahak/core/dist/icons/whatsapp_icon.svg";
import Facebook from "@vahak/core/dist/icons/FacebookGrey.svg";
import Skeleton from "@vahak/core/dist/components/skeleton/Skeleton";

// Types
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { UserLorryInfoTypes } from "@vahak/core/dist/custom-types/lorry";
import { MarketType } from "@vahak/core/dist/custom-types/market-type";

import ModalOrBottomSheetWrapper from "@vahak/core-ui/dist/components/ModalOrBottomSheetWrapper";
import {
    generateMarketplaceCardLongDynamicLink,
    getShortLinkBaseUrl
} from "../../../../services/sharableShortLink/helper";
import { getLoadShareText, getLorryShareText } from "./getLoadLorryShareUrl";
import { SHARE_APIS } from "./shareApis";
import API from "@vahak/core/dist/_services/const";
import { IEventTrackerProp } from "../../../../types/events";
import { LoadScreenEvents } from "@vahak/core/dist/constants/event-names";
import { LoadAllDetails } from "../../../../services/load/useGetLoadAllDetailsV2/type";

export interface ShareModalDataType {
    id: number;
    source: string;
    destination?: string;
    quantity?: number;
    lorry_type_id?: number;
    lorry_type_text?: string;
    transporter_name: string;
    capacity?: number;
    capacity_unit?: string;
    name?: string;
    current_city_name?: string;
}

interface BaseShareModalProps extends IEventTrackerProp {
    toggle: any;
    isOpen: boolean;
    modalTitle?: string;
    isCustomLink?: boolean;
}

interface copyLinkProps extends BaseShareModalProps {
    isCustomLink: true;
    copyLinkURL: string;
    facebookShareText: string;
    whatsappShareText: string;
}
interface marketplaceShareModalProps extends BaseShareModalProps {
    isCustomLink?: false;
    data?: any;
    lorryInfo?: UserLorryInfoTypes;
    loadInfo?: LoadAllDetails;
    mktType: MarketType;
    needActivation?: boolean;
    isSharingDisabled?: boolean;
    onSuccess?: () => void;
}
type ShareModalProps = copyLinkProps | marketplaceShareModalProps;
const ShareModal = ({ toggle, isOpen, modalTitle, isCustomLink = false, trackEvent, ...props }: ShareModalProps) => {
    const { referralCode } = useContext(AppContext);
    const { copyLinkURL, facebookShareText, whatsappShareText } = props as copyLinkProps;
    const {
        data: shareData,
        lorryInfo,
        loadInfo,
        mktType = "load",
        needActivation = false,
        isSharingDisabled = false,
        onSuccess
    } = props as marketplaceShareModalProps;
    const { t } = useTranslation("common");
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [whatsapptext, setWhatsappText] = useState<string | undefined>(undefined);
    const [fbText, setFbText] = useState<string | undefined>(undefined);

    const [isLoadingUrlData, setIsloadingUrlData] = useState(true);

    const [src, setSrc] = useState<CitySearchResultWithCateg>();
    const [isShareCtaPressed, setIsShareCtaPressed] = useState(false);

    useEffect(() => {
        if (url && fbText && whatsapptext) {
            setIsloadingUrlData(false);
        } else if (copyLinkURL && whatsappShareText && facebookShareText) {
            setIsloadingUrlData(false);
            setUrl(copyLinkURL);
            setFbText(facebookShareText);
            setWhatsappText(whatsappShareText);
        }
    }, [url, fbText, whatsapptext, copyLinkURL, whatsappShareText, facebookShareText]);

    useEffect(() => {
        if (!shareData && !lorryInfo && !loadInfo) {
            return;
        }
        if (needActivation && !isShareCtaPressed) return;

        (async () => {
            const utm_params = mktType === "load" ? "web_load_share" : "web_lorry_share";

            const link =
                mktType === "load"
                    ? generateMarketplaceCardLongDynamicLink({
                          mktType: "load",
                          source: cityStringIncludeParentheses(
                              shareData?.source || shareData?.source_text || loadInfo?.load_detail?.source
                          ),
                          destination: cityStringIncludeParentheses(
                              shareData?.destination ||
                                  shareData?.destination_text ||
                                  loadInfo?.load_detail?.destination
                          ),
                          entityId: shareData?.id || loadInfo?.load_detail?.id,
                          oppcmpid:
                              shareData?.oppositeCompanyId ||
                              shareData?.transporter_id ||
                              loadInfo?.load_detail?.company_id,
                          referralCode: referralCode ?? "",
                          utm_campaign: utm_params,
                          utm_medium: utm_params,
                          utm_source: utm_params
                      })
                    : generateMarketplaceCardLongDynamicLink({
                          mktType: "lorry",
                          source: cityStringIncludeParentheses(
                              shareData?.source || shareData?.source_text || loadInfo?.load_detail?.source
                          ),
                          destination: cityStringIncludeParentheses(
                              shareData?.destination ||
                                  shareData?.destination_text ||
                                  loadInfo?.load_detail?.destination
                          ),
                          entityId: shareData?.id || lorryInfo?.id,
                          oppcmpid:
                              shareData?.oppositeCompanyId ||
                              shareData?.transporter_id ||
                              loadInfo?.load_detail?.company_id,
                          referralCode: referralCode ?? "",
                          utm_campaign: utm_params,
                          utm_medium: utm_params,
                          utm_source: utm_params
                      });
            const requestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    dynamicLinkInfo: {
                        domainUriPrefix: getShortLinkBaseUrl(),
                        link,
                        androidInfo: {
                            androidPackageName:
                                "search.find.lorries.invite.transporter.add.lorries.deliveries.codenicely.search.vahak",
                            androidFallbackLink: link,
                            androidMinPackageVersionCode: ""
                        },
                        iosInfo: {
                            iosBundleId: "",
                            iosFallbackLink: "",
                            iosCustomScheme: "",
                            iosIpadFallbackLink: "",
                            iosIpadBundleId: "",
                            iosAppStoreId: ""
                        },
                        analyticsInfo: {
                            googlePlayAnalytics: {
                                utmSource: utm_params,
                                utmMedium: utm_params,
                                utmCampaign: utm_params
                            },
                            itunesConnectAnalytics: {
                                at: "",
                                ct: "",
                                mt: "",
                                pt: ""
                            }
                        },
                        socialMetaTagInfo: {
                            socialTitle: "",
                            socialDescription: "",
                            socialImageLink: ""
                        }
                    },
                    suffix: {
                        option: "short"
                    }
                })
            };
            fetch(API.GET_SHORT_LINK + `key=AIzaSyBvHW1U7rzdZp2NQuzpSCFgGy87__ShkfE`, requestOptions)
                .then((response) => response.json())
                .then(async (data) => {
                    if (mktType === "load") {
                        setUrl(data.shortLink);
                        if (data.shortLink) {
                            setWhatsappText(
                                await getLoadShareText(shareData || lorryInfo || loadInfo!, false, data.shortLink)
                            );
                            setFbText(
                                await getLoadShareText(shareData || lorryInfo || loadInfo!, false, data.shortLink)
                            );
                        }
                    } else {
                        setUrl(data.shortLink);
                        if (data.shortLink) {
                            setWhatsappText(
                                await getLorryShareText(shareData || lorryInfo || loadInfo!, false, data.shortLink)
                            );
                            setFbText(
                                await getLorryShareText(shareData || lorryInfo || loadInfo!, false, data.shortLink)
                            );
                        }
                    }
                });
        })();
    }, [shareData, loadInfo, lorryInfo, isShareCtaPressed]);

    const onClickCopy = async () => {
        if (!url) {
            return;
        }
        copyToClipboard(url, t("share_modal_link_copied"));
    };

    const activateLorry = async () => {
        if (!isSharingDisabled) {
            setIsShareCtaPressed(true);
        } else {
            if (onSuccess) onSuccess();
            else toggle();
        }
    };

    return (
        <>
            <ModalOrBottomSheetWrapper
                isOpen={isOpen}
                onToggle={toggle}
                modalSize="fit"
                titleText={(isShareCtaPressed && t("share_lorry")) || modalTitle}
            >
                <div className={Styles.modalDialog}>
                    <div className={Styles.modalBody}>
                        {needActivation && !isShareCtaPressed && (shareData || lorryInfo || loadInfo) ? (
                            <Activation
                                data={shareData!}
                                lorryInfo={lorryInfo!}
                                src={src}
                                setSrc={setSrc}
                                toggle={toggle}
                                activateLorry={activateLorry}
                                isSharingDisabled={isSharingDisabled}
                            />
                        ) : (
                            <div className={classNames(Styles.BodyContentWrapper)}>
                                {/* Copy URl container */}
                                <div className={classNames(Styles.copyLinkContainer)}>
                                    <AppBtn
                                        btnType="submit"
                                        className={classNames(Styles.copyLinkButton)}
                                        onClick={onClickCopy}
                                    >
                                        {!isLoadingUrlData ? (
                                            <>
                                                <span className={Styles.url}>{url}</span>
                                                <span className={Styles.btnText}>{t("share_modal_copy_url")}</span>
                                            </>
                                        ) : (
                                            <Skeleton />
                                        )}
                                    </AppBtn>
                                </div>
                                {/* Social share container */}
                                <div className={classNames(Styles.socialShareContainer)}>
                                    <div className={classNames(Styles.socialMediaShare)}>
                                        <h3 className={Styles.socialMediaShareCol}>
                                            {!isLoadingUrlData ? (
                                                t("share_modal_share_it_on")
                                            ) : (
                                                <Skeleton width="103px" />
                                            )}
                                        </h3>
                                        <A
                                            className={Styles.socialMediaShareCol}
                                            href={
                                                window.navigator.userAgent.toLowerCase().includes("mobile")
                                                    ? SHARE_APIS.whatsappPhone(whatsapptext)
                                                    : SHARE_APIS.whatsappDesktop(whatsapptext)
                                            }
                                            openInNewTab={true}
                                        >
                                            <span
                                                onClick={() => {
                                                    trackEvent?.(LoadScreenEvents["sharedonwhatsapp"]);
                                                }}
                                            >
                                                <div className={classNames(Styles.whatsappShare)}>
                                                    {!isLoadingUrlData ? <Whatsapp /> : <Skeleton height="80px" />}
                                                </div>
                                                <h5 className={Styles.socialName}>
                                                    {!isLoadingUrlData ? t("whatsApp") : <Skeleton />}
                                                </h5>
                                            </span>
                                        </A>

                                        <A
                                            className={Styles.socialMediaShareCol}
                                            href={SHARE_APIS.fb(fbText, url)}
                                            openInNewTab={true}
                                        >
                                            <div className={classNames(Styles.facebookShare)}>
                                                {!isLoadingUrlData ? <Facebook /> : <Skeleton height="80px" />}
                                            </div>
                                            <h5 className={Styles.socialName}>
                                                {!isLoadingUrlData ? t("facebook") : <Skeleton />}
                                            </h5>
                                        </A>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </ModalOrBottomSheetWrapper>
        </>
    );
};

export default ShareModal;
